<script setup lang="ts">
import type { IConversationModel } from '@tencentcloud/chat-uikit-engine';
import { TUIConversationService, StoreName, TUIStore } from '@tencentcloud/chat-uikit-engine';
import type { PropType } from 'vue';
import debounce from 'lodash/debounce';
import { message, Modal } from 'ant-design-vue';
import { sendMessages } from '../utils/sendMessage';
import ConsultInfoForm from '../../consult-info-form/index.vue';
import type { ITipTapEditorContent } from '@/TUIKit/interface';
import { exchangeCustomerService, closeSession, listEnableKfShop, listShopKf } from '@/api/session';
import { useSessionStore } from '@/store/modules/session';
import { useUserStore } from '@/store/modules/user';
import { useSessionConsultInfoVisible } from '@/views/session/hooks';
import { getExchangeKfGroup } from '@/api/platform-service';

const props = defineProps({
    conversation: {
        type: Object as PropType<IConversationModel>,
        default: () => ({})
    }
});
let currentGroupId = '';

const typingStatus = ref(false);
const platformGroupLoading = ref(false);
const merchantKfLoading = ref(false);
const currentConversation = ref<IConversationModel>(props.conversation);
const platformGroupList = ref<PLATFORM_SERVICE_API.ExchangeKfGroupResp[]>([]);
const merchantKfList = ref<Array<SESSION_API.MerchantKfShopOption | SESSION_API.ShopKfOption>>([]);
const endModalOpen = ref(false);
const transferModalOpen = ref(false);
const endConfirmLoading = ref(false);
const transferConfirmLoading = ref(false);
const transferType = ref<'platform' | 'merchant'>('platform');
const platformGroupId = ref<string>();
const merchantKfImId = ref<string>();
const endConsultFormRef = ref<InstanceType<typeof ConsultInfoForm>>();
const transferConsultFormRef = ref<InstanceType<typeof ConsultInfoForm>>();

const sessionStore = useSessionStore();
const userStore = useUserStore();
const { showSessionConsultInfo } = useSessionConsultInfoVisible();
const isPlatformKf = computed(() => userStore.userInfo.kfType === 1);
const canShowTransfer = computed(
    () =>
        userStore.userInfo.kfType !== undefined ||
        userStore.userInfo.isSpKf !== undefined ||
        Boolean(userStore.userInfo.kfId || userStore.userInfo.kfImId)
);
const showName = computed(() => {
    const nickName = sessionStore.groupUserInfo?.nickName ?? '';
    const identity = sessionStore.conversationUserInfo?.identity ?? '';
    const str = identity ? `${nickName}@${identity}` : nickName;

    return typingStatus.value ? '对方正在输入...' : str;
});
const competitionType = computed(() => {
    return sessionStore.groupUserInfo?.competitionType ?? '';
});
const showMerchantToPlatformTip = computed(
    () => !isPlatformKf.value && transferType.value === 'platform'
);
const shouldShowPlatformGroup = computed(
    () => transferType.value === 'platform' && isPlatformKf.value
);
const shouldShowTransferConsultForm = computed(() => showSessionConsultInfo.value);
const merchantKfPlaceholder = computed(() =>
    isPlatformKf.value ? '请选择商家' : '请选择客服人员'
);

async function loadPlatformGroupList() {
    try {
        platformGroupLoading.value = true;
        platformGroupList.value = await getExchangeKfGroup();
    } finally {
        platformGroupLoading.value = false;
    }
}

async function fetchMerchantKfList(keyword?: string) {
    try {
        merchantKfLoading.value = true;
        merchantKfList.value = isPlatformKf.value
            ? await listEnableKfShop({
                  keyword
              })
            : await listShopKf({
                  keyword
              });
    } finally {
        merchantKfLoading.value = false;
    }
}

const searchMerchantKf = debounce(fetchMerchantKfList, 300);

function getExplicitBool(
    option: SESSION_API.MerchantKfShopOption | SESSION_API.ShopKfOption,
    fields: string[]
) {
    const optionRecord = option as Record<string, unknown>;
    for (const field of fields) {
        const value = optionRecord[field];
        if (value !== undefined && value !== null) {
            return value === true || value === 1;
        }
    }
    return undefined;
}

function isMerchantKfOnline(option: SESSION_API.MerchantKfShopOption | SESSION_API.ShopKfOption) {
    return getExplicitBool(option, ['kfOnline', 'online', 'isOnline', 'status']) ?? true;
}

function isMerchantTakeoverEnabled(option: SESSION_API.MerchantKfShopOption) {
    return (
        getExplicitBool(option, [
            'enableTakeOver',
            'takeOverStatus',
            'selfServiceStatus',
            'serviceStatus'
        ]) ?? true
    );
}

function isMerchantKfDisabled(option: SESSION_API.MerchantKfShopOption | SESSION_API.ShopKfOption) {
    if (!isMerchantKfOnline(option)) {
        return true;
    }
    if (isPlatformKf.value && !isMerchantTakeoverEnabled(option)) {
        return true;
    }
    return false;
}

function getMerchantOptionText(
    option: SESSION_API.MerchantKfShopOption | SESSION_API.ShopKfOption
) {
    if (!isMerchantKfOnline(option)) {
        return `${option.desc}（离线）`;
    }
    if (isPlatformKf.value && !isMerchantTakeoverEnabled(option)) {
        return `${option.desc}（未开启自主承接）`;
    }
    return option.desc;
}

const merchantTransferTip = computed(() => {
    if (!isPlatformKf.value || transferType.value !== 'merchant') {
        return '';
    }

    const selectedMerchantKf = merchantKfList.value.find(
        item => item.code === merchantKfImId.value
    );
    if (!selectedMerchantKf) {
        return '';
    }
    if (!isMerchantKfOnline(selectedMerchantKf)) {
        return '商家客服未在线';
    }
    if (!isMerchantTakeoverEnabled(selectedMerchantKf)) {
        return '商家客服未开通自主承接';
    }

    return '';
});

function resolveMerchantShop(
    option: SESSION_API.MerchantKfShopOption | SESSION_API.ShopKfOption
): SESSION_API.EnableKfShopOption {
    const shop = option as SESSION_API.MerchantKfShopOption;
    const shopId = shop.shopId ?? shop.exchangeInShopId;
    const shopName = shop.shopName ?? shop.exchangeInShopName;

    return {
        code: shopId ?? option.code,
        desc: shopName ?? option.desc
    };
}

async function afterTransferSuccess(options?: { removeCurrentConversation?: boolean }) {
    if (options?.removeCurrentConversation ?? true) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        await TUIConversationService.deleteConversation({
            conversationIDList: [currentConversation.value.conversationID],
            clearHistoryMessage: false
        });
        await TUIConversationService.switchConversation('');
    }
    message.success('转接成功');
}

async function prefillMerchantKf() {
    if (!isPlatformKf.value) {
        await fetchMerchantKfList('');
        return;
    }

    const shop = transferConsultFormRef.value?.getSelectedShop();
    if (!shop?.desc) {
        await fetchMerchantKfList('');
        return;
    }

    await fetchMerchantKfList(shop.desc);
    const matched =
        merchantKfList.value.find(item => item.desc === shop.desc) ?? merchantKfList.value[0];
    merchantKfImId.value = matched?.code;
    if (matched) {
        await handleMerchantKfChange(matched.code);
    }
}

async function openTransferModal() {
    transferType.value = 'platform';
    platformGroupId.value = undefined;
    merchantKfImId.value = undefined;
    merchantKfList.value = [];
    transferModalOpen.value = true;
    await nextTick();
    await Promise.all([
        isPlatformKf.value ? loadPlatformGroupList() : Promise.resolve(),
        shouldShowTransferConsultForm.value
            ? transferConsultFormRef.value?.identifyShop({
                  silent: true,
                  overwrite: false
              })
            : Promise.resolve()
    ]);
}

async function handleTransferOk() {
    try {
        transferConfirmLoading.value = true;
        if (transferType.value === 'platform') {
            const consultPayload = shouldShowTransferConsultForm.value
                ? await transferConsultFormRef.value?.validate()
                : undefined;
            if (isPlatformKf.value) {
                const selectedGroup = platformGroupList.value.find(
                    item => item.id === platformGroupId.value
                );
                if (!selectedGroup) {
                    message.error('请选择技能组');
                    return;
                }
                if (selectedGroup.status === 0) {
                    message.error('当前技能组不可转接');
                    return;
                }
            }

            await exchangeCustomerService({
                groupId: currentGroupId,
                type: isPlatformKf.value ? 2 : 3,
                kfGroupId: isPlatformKf.value ? platformGroupId.value : undefined,
                consultProblemType: consultPayload?.consultProblemType,
                exchangeInShopId: consultPayload?.exchangeInShopId
            });
            transferModalOpen.value = false;
            await afterTransferSuccess({
                removeCurrentConversation: isPlatformKf.value
            });
            return;
        }

        if (!merchantKfImId.value) {
            message.error(isPlatformKf.value ? '请选择商家' : '请选择商家客服');
            return;
        }
        const selectedMerchantKf = merchantKfList.value.find(
            item => item.code === merchantKfImId.value
        );
        if (selectedMerchantKf) {
            if (!isMerchantKfOnline(selectedMerchantKf)) {
                message.error(isPlatformKf.value ? '商家客服未在线' : '该商家客服当前不在线');
                return;
            }
            if (isPlatformKf.value && !isMerchantTakeoverEnabled(selectedMerchantKf)) {
                message.error('商家客服未开通自主承接');
                return;
            }
        }

        const consultPayload = shouldShowTransferConsultForm.value
            ? await transferConsultFormRef.value?.validate()
            : undefined;
        await exchangeCustomerService({
            groupId: currentGroupId,
            type: isPlatformKf.value ? 4 : 1,
            customerImId: isPlatformKf.value ? undefined : merchantKfImId.value,
            consultProblemType: consultPayload?.consultProblemType,
            exchangeInShopId: consultPayload?.exchangeInShopId
        });
        transferModalOpen.value = false;
        await afterTransferSuccess({
            removeCurrentConversation: !isPlatformKf.value
        });
    } catch (error) {
        console.error(error);
    } finally {
        transferConfirmLoading.value = false;
    }
}

watch(transferType, async type => {
    if (transferModalOpen.value && type === 'merchant') {
        await nextTick();
        await prefillMerchantKf();
    }
});

async function handleMerchantKfChange(value?: string) {
    const selected = merchantKfList.value.find(item => item.code === value);
    if (!selected) {
        transferConsultFormRef.value?.setSelectedShop(undefined);
        return;
    }
    if (!shouldShowTransferConsultForm.value) {
        return;
    }

    const shop = resolveMerchantShop(selected);
    transferConsultFormRef.value?.setSelectedShop(shop);
}

TUIStore.watch(StoreName.CHAT, {
    typingStatus: (status: boolean) => {
        typingStatus.value = status;
    }
});

watch(
    () => props.conversation.conversationID,
    async () => {
        currentConversation.value = props.conversation;
        currentGroupId = props.conversation.groupProfile?.groupID;
    },
    { immediate: true }
);

// 完结会话
async function finishSession(
    payload?: Pick<SESSION_API.CloseSessionReq, 'consultProblemType' | 'exchangeInShopId'>
) {
    const editorContentList = [
        {
            type: 'text',
            payload: {
                text: '若没有其他问题，这边就先不打扰您了。稍后系统将自动结束本次会话（无需回复）\n如还需帮助，欢迎随时联系'
            }
        }
    ] as ITipTapEditorContent[];
    await sendMessages(editorContentList, currentConversation.value);

    const res = await closeSession({
        groupId: currentGroupId,
        ...(payload ?? {})
    });
    if (res) {
        message.success('完结会话成功');
    } else {
        message.error('完结会话失败');
    }
    userStore.updateServiceStatusInfo();
    return res;
}

const handleEndSession = async () => {
    if (!showSessionConsultInfo.value) {
        Modal.confirm({
            title: '提示',
            content: '确定完结会话吗？',
            async onOk() {
                try {
                    await finishSession();
                } catch (error) {
                    console.error(error);
                }
            }
        });
        return;
    }

    endModalOpen.value = true;
    await nextTick();
    await endConsultFormRef.value?.identifyShop({
        silent: true,
        overwrite: true
    });
};

const handleEndSessionOk = async () => {
    try {
        endConfirmLoading.value = true;
        const consultPayload = await endConsultFormRef.value?.validate();
        const res = await finishSession({
            consultProblemType: consultPayload?.consultProblemType,
            exchangeInShopId: consultPayload?.exchangeInShopId
        });
        if (res) {
            endModalOpen.value = false;
        }
    } catch (error) {
        console.error(error);
    } finally {
        endConfirmLoading.value = false;
    }
};
</script>

<template>
    <div class="h-46px px-4 shadow flex-bc">
        <div class="max-w-1/2 truncate font-500">
            <div>
                {{ showName }}
            </div>
            <div
                v-if="competitionType"
                class="color-#8c5af0 border-solid border-#8c5af0 border-1px rounded-4px pt-2px text-center pb-2px w-40px text-10px mt-2px"
            >
                三创赛
            </div>
        </div>
        <div>
            <a-button
                type="link"
                @click.stop="handleEndSession"
            >
                完结会话
            </a-button>
            <a-button
                v-if="canShowTransfer"
                type="link"
                @click.stop="openTransferModal"
            >
                转接
            </a-button>
            <!-- <a-divider type="vertical" /> -->
            <!-- <a-button type="link"> 退出接待 </a-button> -->
        </div>
    </div>
    <a-modal
        v-if="showSessionConsultInfo"
        v-model:open="endModalOpen"
        title="完结会话"
        ok-text="确认完结"
        cancel-text="取消"
        destroy-on-close
        :confirm-loading="endConfirmLoading"
        @ok="handleEndSessionOk"
    >
        <ConsultInfoForm
            ref="endConsultFormRef"
            :group-id="currentGroupId"
            :show-save="false"
            :auto-identify="true"
            :label-col="{ span: 6 }"
            :wrapper-col="{ span: 18 }"
        />
    </a-modal>
    <a-modal
        v-model:open="transferModalOpen"
        title="转接"
        ok-text="确认转接"
        cancel-text="取消"
        destroy-on-close
        :width="640"
        :confirm-loading="transferConfirmLoading"
        @ok="handleTransferOk"
    >
        <a-form
            :label-col="{ span: 5 }"
            :wrapper-col="{ span: 19 }"
        >
            <a-form-item label="转至">
                <a-radio-group v-model:value="transferType">
                    <template v-if="isPlatformKf">
                        <a-radio value="platform">平台客服</a-radio>
                        <a-radio value="merchant">商家客服</a-radio>
                    </template>
                    <template v-else>
                        <a-radio value="merchant">商家客服</a-radio>
                        <a-radio value="platform">平台客服</a-radio>
                    </template>
                </a-radio-group>
            </a-form-item>
            <a-form-item
                v-if="shouldShowPlatformGroup"
                label="技能组"
                required
            >
                <a-select
                    v-model:value="platformGroupId"
                    :loading="platformGroupLoading"
                    placeholder="请选择技能组"
                    allow-clear
                    @focus="loadPlatformGroupList"
                >
                    <a-select-option
                        v-for="item in platformGroupList"
                        :key="item.id"
                        :value="item.id"
                        :disabled="item.status === 0"
                    >
                        <a-badge
                            :status="item.status === 1 ? 'success' : 'default'"
                            :text="item.name"
                        />
                    </a-select-option>
                </a-select>
            </a-form-item>
            <a-form-item
                v-if="transferType === 'merchant'"
                label="商家客服"
                required
            >
                <a-select
                    v-model:value="merchantKfImId"
                    show-search
                    :filter-option="false"
                    :loading="merchantKfLoading"
                    :placeholder="merchantKfPlaceholder"
                    allow-clear
                    @focus="searchMerchantKf('')"
                    @search="searchMerchantKf"
                    @change="handleMerchantKfChange"
                >
                    <a-select-option
                        v-for="item in merchantKfList"
                        :key="item.code"
                        :value="item.code"
                        :disabled="isMerchantKfDisabled(item)"
                    >
                        <a-badge
                            :status="isMerchantKfOnline(item) ? 'success' : 'default'"
                            :text="getMerchantOptionText(item)"
                        />
                    </a-select-option>
                </a-select>
            </a-form-item>
            <div
                v-if="merchantTransferTip"
                class="color-#ff4d4f mb-16px"
            >
                {{ merchantTransferTip }}
            </div>
        </a-form>
        <div
            v-if="showMerchantToPlatformTip"
            class="color-#ff4d4f mb-16px"
        >
            若咨询为商家相关，平台代为处理将按每通收取服务费（目前试运行不收费）
        </div>
        <ConsultInfoForm
            v-if="shouldShowTransferConsultForm"
            ref="transferConsultFormRef"
            :group-id="currentGroupId"
            :show-save="false"
            :auto-identify="true"
            :disabled-shop="transferType === 'merchant'"
            :force-related-shop="transferType === 'merchant'"
            :show-related-shop="transferType !== 'platform'"
            :show-identify="transferType !== 'merchant'"
            :label-col="{ span: 5 }"
            :wrapper-col="{ span: 19 }"
        />
    </a-modal>
</template>
